import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import styled, { css, keyframes } from 'styled-components';
import {
  AudioLines,
  Check,
  ChevronDown,
  CircleCheck,
  CirclePause,
  Copy,
  FileText,
  Image as ImageIcon,
  Inbox,
  LoaderCircle,
  Lock,
  Pencil,
  Play,
  RotateCcw,
  Trash2,
  UserPlus,
  Video,
} from 'lucide-react';
import TicketMessages from '../../tickets/TicketMessages';
import TicketVideosSection from '../../tickets/TicketVideosSection';
import TicketAudiosSection from '../../tickets/TicketAudiosSection';
import TicketImagesSection from '../../tickets/TicketImagesSection';
import TicketPdfSection from '../../tickets/TicketPdfSection';
import '../../../styles/components/SupportTicketDetails.css';
import { useApp } from '../../../context/AppContext';
import ResponsibleMultiSelect from '../../shared/ResponsibleMultiSelect';
import CompanyNameCombobox from '../../shared/CompanyNameCombobox';
import { formatTicketProtocol } from '../../../utils/ticketDisplayUtils';
import {
  getAppOriginColor,
  getAppOriginFilterOptions,
  getAppOriginLabel,
} from '../../../services/appOriginService';
import messageService from '../../../services/messageService';
import {
  updateResponsibleUsers,
  getTicketById,
  deleteTicket,
  updateTicketDetails,
  reopenTicket,
  getTicketImages,
  getTicketPdfs,
  getTicketVideos,
  getTicketAudios,
} from '../../../services/ticketService';
import {
  getStatusLabel,
  getStatusClass,
  getStatusCategory,
  isTerminalStatus,
  isReopenTargetStatus,
  requiresResolutionCapture,
  findFirstStatusCodeByCategory,
  getStatusFilterOptions,
} from '../../../services/statusService';
import { getPriorityLabel, PRIORITY_FILTER_OPTIONS } from '../../../services/priorityService';
import {
  getDemandTypeLabel,
  DEMAND_TYPE_FILTER_OPTIONS,
} from '../../../services/demandTypeService';
import { getResolutionTypeLabel } from '../../../services/resolutionTypeService';
import ResolveTicketModal from './ResolveTicketModal';
import { encodeDescriptionChangeMessage } from '../../../utils/descriptionChangeMessage';

const ATTACHMENT_TABS = [
  { id: 'images', label: 'Imagens', icon: ImageIcon },
  { id: 'pdf', label: 'PDFs', icon: FileText },
  { id: 'videos', label: 'Vídeos', icon: Video },
  { id: 'audios', label: 'Áudios', icon: AudioLines },
];

const ATTACHMENT_LOADERS = {
  images: getTicketImages,
  pdf: getTicketPdfs,
  videos: getTicketVideos,
  audios: getTicketAudios,
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const date = parseISO(dateString);
  return isValid(date) ? date : null;
};

const formatDateTime = (dateString) => {
  const date = parseDate(dateString);
  return date ? format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—';
};

const formatRelative = (dateString) => {
  const date = parseDate(dateString);
  return date ? formatDistanceToNowStrict(date, { addSuffix: true, locale: ptBR }) : '';
};

const formatDuration = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  if (total < 60) return `${total} min`;
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  return mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, 12px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const focusRing = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  min-width: 0;
  padding-bottom: 5rem;
  box-sizing: border-box;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1.5rem;
  border: 1px dashed var(--color-border-secondary);
  border-radius: 12px;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  text-align: center;
`;

const Header = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 1rem 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
`;

const ProtocolChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 24px;
  padding: 0 0.5rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 6px;
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  svg {
    flex-shrink: 0;
    opacity: 0.7;
  }

  &:hover {
    border-color: var(--color-border-secondary);
    color: var(--color-text-primary);
  }

  ${focusRing}
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
`;

const StatusPill = styled(Pill)`
  background: ${({ $status }) => `var(--badge-status-${$status}-bg, var(--badge-status-open-bg))`};
  color: ${({ $status }) => `var(--badge-status-${$status}-fg, var(--badge-status-open-fg))`};
  -webkit-text-fill-color: ${({ $status }) =>
    `var(--badge-status-${$status}-fg, var(--badge-status-open-fg))`};
`;

const PriorityPill = styled(Pill)`
  background: ${({ $priority }) => `var(--badge-priority-${$priority}-bg)`};
  color: ${({ $priority }) => `var(--badge-priority-${$priority}-fg)`};
  -webkit-text-fill-color: ${({ $priority }) => `var(--badge-priority-${$priority}-fg)`};
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.25rem, 1.05rem + 0.6vw, 1.625rem);
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: -0.015em;
  color: var(--color-text-primary);
  overflow-wrap: anywhere;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const MetaSeparator = styled.span`
  color: var(--color-border-accent);
`;

const OriginDot = styled.i`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $color }) => $color || 'var(--color-border-accent)'};
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.875rem;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease,
    transform 0.1s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: var(--color-border-secondary);
    background: var(--color-hover-overlay);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${focusRing}

  ${({ $variant }) =>
    $variant === 'primary' &&
    css`
      background: var(--color-btn-primary-bg);
      border-color: var(--color-btn-primary-border);
      color: var(--color-btn-primary-text);

      &:hover:not(:disabled) {
        background: var(--color-btn-primary-bg);
        border-color: var(--color-btn-primary-border);
        opacity: 0.88;
      }
    `}

  ${({ $variant }) =>
    $variant === 'danger' &&
    css`
      border-color: rgba(220, 53, 69, 0.25);
      color: #dc3545;

      &:hover:not(:disabled) {
        border-color: rgba(220, 53, 69, 0.45);
        background: rgba(220, 53, 69, 0.08);
      }
    `}

  ${({ $size }) =>
    $size === 'sm' &&
    css`
      height: 30px;
      padding: 0 0.625rem;
      font-size: 0.78rem;
    `}

  ${({ $ghost }) =>
    $ghost &&
    css`
      border-color: transparent;
      background: transparent;
      color: var(--color-text-secondary);

      &:hover:not(:disabled) {
        border-color: transparent;
        color: var(--color-text-primary);
      }
    `}
`;

const Spinner = styled(LoaderCircle)`
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2s;
  }
`;

const SelectWrap = styled.div`
  position: relative;
  display: flex;
  width: ${({ $inline }) => ($inline ? 'auto' : '100%')};
  min-width: 0;

  select {
    width: 100%;
    height: ${({ $inline }) => ($inline ? '36px' : '34px')};
    padding: 0 2rem 0 0.75rem;
    border: 1px solid var(--color-border-primary);
    border-radius: 8px;
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-family: inherit;
    font-size: 0.8125rem;
    font-weight: ${({ $inline }) => ($inline ? 600 : 400)};
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  select:hover:not(:disabled) {
    border-color: var(--color-border-secondary);
  }

  select:focus {
    outline: none;
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  select:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  > svg {
    position: absolute;
    right: 0.625rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }
`;

const Notice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  font-size: 0.8125rem;
  line-height: 1.5;

  > svg {
    flex-shrink: 0;
    color: var(--color-text-primary);
  }

  > span {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  align-items: start;
  gap: 1.25rem;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const AsideColumn = styled(Column)`
  @media (max-width: 1100px) {
    order: -1;
  }
`;

const Card = styled.section`
  min-width: 0;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-primary);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  scroll-margin-top: 1rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 52px;
  padding: 0.5rem 0.75rem 0.5rem 1.125rem;
  border-bottom: 1px solid var(--color-border-primary);
  box-sizing: border-box;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--color-text-primary);
`;

const CardBody = styled.div`
  padding: 1.125rem;

  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const DescriptionText = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: ${({ $empty }) => ($empty ? 'var(--color-text-muted)' : 'var(--color-text-primary)')};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const DescriptionTextarea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 180px;
  box-sizing: border-box;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 0.9375rem;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const EditFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.625rem;
  font-size: 0.75rem;
  color: var(--color-text-muted);
`;

const Tabs = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  max-width: 100%;
  gap: 0.25rem;
  padding: 0.25rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 9px;
  background: var(--color-bg-secondary);
  box-sizing: border-box;
`;

const badgePop = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  70% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); }
`;

const TabBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 0.3rem;
  border: 2px solid var(--color-bg-primary);
  border-radius: 999px;
  background: #dc3545;
  color: #fff;
  -webkit-text-fill-color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  box-sizing: border-box;
  pointer-events: none;
  animation: ${badgePop} 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Tab = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 30px;
  padding: 0 0.75rem;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: var(--color-text-primary);
  }

  &[aria-selected='true'] {
    background: var(--color-bg-primary);
    color: var(--color-text-primary);
    font-weight: 600;
    box-shadow: var(--shadow-sm);
  }

  ${focusRing}
`;

const AttachmentPanel = styled.div`
  margin-top: 1rem;

  > section {
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
  }
`;

const ConversationBody = styled(CardBody)`
  .ticket-messages {
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
  }

  .ticket-messages > h3 {
    display: none;
  }
`;

const PropertyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const PropertyRow = styled.div`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  min-height: 36px;

  @media (max-width: 1100px) and (min-width: 641px) {
    grid-template-columns: 160px minmax(0, 1fr);
  }

  ${({ $stacked }) =>
    $stacked &&
    css`
      && {
        grid-template-columns: minmax(0, 1fr);
        gap: 0.5rem;
        margin-top: 0.5rem;
        padding-top: 0.875rem;
        border-top: 1px solid var(--color-border-primary);
      }
    `}
`;

const PropertyLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const ChangedDot = styled.span`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-text-primary);
`;

const ReadValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  font-size: 0.8125rem;
  color: ${({ $muted }) => ($muted ? 'var(--color-text-muted)' : 'var(--color-text-primary)')};
  overflow-wrap: anywhere;
`;

const CompanyField = styled.div`
  min-width: 0;

  && input {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }

  div:has(> input) {
    min-height: 34px;
  }

  && button {
    width: 32px;
    border-left: none;
    color: var(--color-text-muted);
    font-size: 0.625rem;
  }
`;

const ResponsibleField = styled.div`
  min-width: 0;

  .responsible-container > .label {
    display: none;
  }

  #ticket-details-responsible {
    width: 100%;
    min-width: 0;
    min-height: 38px;
    padding: 0.5rem 0.625rem;
    align-items: center;
    border-radius: 8px;
    box-shadow: none;
    font-size: 0.8125rem;
    font-weight: 400;
    text-align: left;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  }

  #ticket-details-responsible:hover:not(:disabled) {
    border-color: var(--color-border-secondary);
    background: var(--color-hover-overlay);
  }

  #ticket-details-responsible + div {
    right: 0;
    min-width: 0;
  }

  #ticket-details-responsible:focus {
    border-color: var(--color-text-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const PropertyCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 0.3rem;
  border-radius: 999px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  font-size: 0.6875rem;
  font-weight: 600;
  box-sizing: border-box;
`;

const PeopleList = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
`;

const PersonRow = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-primary);

  > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PersonInitial = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  font-size: 0.6875rem;
  font-weight: 600;
  box-sizing: border-box;
`;

const AssignPlaceholder = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  color: var(--color-text-muted);
`;

const InfoList = styled.dl`
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  gap: 0.625rem 0.75rem;
  margin: 0;
  font-size: 0.8125rem;

  dt {
    color: var(--color-text-secondary);
    font-weight: 500;
  }

  dd {
    margin: 0;
    min-width: 0;
    color: var(--color-text-primary);
    overflow-wrap: anywhere;
  }

  @media (max-width: 1100px) and (min-width: 641px) {
    grid-template-columns: 160px minmax(0, 1fr);
  }
`;

const NotesText = styled.p`
  margin: 0.75rem 0 0;
  padding: 0.75rem;
  border-radius: 8px;
  background: var(--color-bg-secondary);
  font-size: 0.8125rem;
  line-height: 1.55;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Timeline = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TimelineItem = styled.li`
  position: relative;
  padding: 0 0 0.875rem 1.25rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.3rem;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    border: 2px solid var(--color-text-primary);
    background: var(--color-bg-primary);
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1.05rem;
    bottom: 0.1rem;
    width: 1px;
    background: var(--color-border-primary);
  }

  &:last-child {
    padding-bottom: 0;
  }

  strong {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  time,
  span {
    display: block;
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: calc(1.25rem + env(safe-area-inset-bottom, 0px));
  left: 50%;
  z-index: 80;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: calc(100vw - 2rem);
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-radius: 12px;
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  font-size: 0.8125rem;
  font-weight: 500;
  box-sizing: border-box;
  transform: translateX(-50%);
  animation: ${slideUp} 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SaveBarText = styled.span`
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none;
  }
`;

const SaveBarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 32px;
  padding: 0 0.75rem;
  border: none;
  border-radius: 8px;
  background: ${({ $primary }) => ($primary ? 'var(--color-bg-primary)' : 'transparent')};
  color: ${({ $primary }) => ($primary ? 'var(--color-text-primary)' : 'inherit')};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  opacity: ${({ $primary }) => ($primary ? 1 : 0.8)};
  transition: opacity 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 1;
    background: ${({ $primary }) => ($primary ? 'var(--color-bg-primary)' : 'rgba(128, 128, 128, 0.2)')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${focusRing}
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const SupportTicketDetails = ({ ticket, onTicketUpdate, onListUpdate, onResetSelection, users }) => {
  const getTicketDescription = (t) => t?.detailedDescription ?? t?.description ?? '';
  // Use um estado interno para o ticket para que as atualizações de status via botões
  // possam ser refletidas imediatamente na UI antes do re-fetch completo.
  // No entanto, ainda vamos re-fetch o ticket completo para garantir que todas as
  // datas de auditoria sejam exibidas corretamente.
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [selectedResponsibleUserIds, setSelectedResponsibleUserIds] = useState(() => {
    const fromList = ticket?.responsibleUsers;
    if (Array.isArray(fromList) && fromList.length > 0) {
      return fromList.map((u) => String(u?.id ?? u)).filter(Boolean);
    }
    const single = ticket?.responsibleUser;
    if (single) return [String(single?.id ?? single)];
    return [];
  });
  const [selectedPriority, setSelectedPriority] = useState(ticket?.priority || '');
  const [selectedAppOrigin, setSelectedAppOrigin] = useState(ticket?.appOrigin || '');
  const [selectedCompanyName, setSelectedCompanyName] = useState(ticket?.companyName || '');
  const [selectedDemandType, setSelectedDemandType] = useState(ticket?.demandType || 'SUPPORT');
  const [selectedDetailedDescription, setSelectedDetailedDescription] = useState(() =>
    getTicketDescription(ticket)
  );
  const [messagesRefreshKey, setMessagesRefreshKey] = useState(0);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [attachmentsTab, setAttachmentsTab] = useState(ATTACHMENT_TABS[0].id);
  const [protocolCopied, setProtocolCopied] = useState(false);
  const [saveBarCenter, setSaveBarCenter] = useState(null);
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const rootRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [changingStatusTo, setChangingStatusTo] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [pendingResolveStatus, setPendingResolveStatus] = useState(null);
  // Acesso ao contexto para usar reloadUsers
  const {
    reloadUsers,
    updateTicketFields,
    addToast,
    currentUser,
    ticketStatuses,
    reloadTicketStatuses,
    appOrigins,
  } = useApp();
  const location = useLocation();

  const ticketDemandType = currentTicket?.demandType || 'SUPPORT';
  const statusOptions = React.useMemo(() => {
    const options = getStatusFilterOptions(ticketDemandType);
    // Backend: reopen só aceita destino INITIAL / IN_PROGRESS / PENDING
    if (isTerminalStatus(currentTicket?.status)) {
      return options.filter((s) => isReopenTargetStatus(s.value));
    }
    return options;
  }, [ticketStatuses, ticketDemandType, currentTicket?.status]);
  const appOriginOptions = React.useMemo(
    () => getAppOriginFilterOptions(),
    [appOrigins]
  );
  const currentCategory = getStatusCategory(currentTicket?.status);
  const codeInProgress = findFirstStatusCodeByCategory('IN_PROGRESS', ticketDemandType);
  const codePending = findFirstStatusCodeByCategory('PENDING', ticketDemandType);
  const codeResolved = findFirstStatusCodeByCategory('RESOLVED', ticketDemandType);
  const codeTerminal = findFirstStatusCodeByCategory('TERMINAL', ticketDemandType);

  // Debug effect para monitorar mudanças nos usuários
  useEffect(() => {
    console.log('SupportTicketDetails - users changed:', users?.length || 0);
  }, [users]);

  useEffect(() => {
    if (location.hash !== '#ticket-messages' || !currentTicket?.id) return;
    const timer = window.setTimeout(() => {
      document.getElementById('ticket-messages')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.hash, currentTicket?.id]);

  const normalizeId = (id) => {
    if (id === null || id === undefined || id === '') return '';
    return String(id);
  };

  const responsibleUserOptions = useMemo(() => {
    const byId = new Map();
    const addUser = (user) => {
      if (!user) return;
      const id = normalizeId(user.id ?? user);
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: user.name || user.email || 'Usuário',
          email: user.email || '',
        });
      }
    };

    (users || []).forEach(addUser);
    (currentTicket?.responsibleUsers || []).forEach(addUser);
    if (currentTicket?.responsibleUser) addUser(currentTicket.responsibleUser);

    return [...byId.values()].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
    );
  }, [users, currentTicket?.responsibleUsers, currentTicket?.responsibleUser]);

  const ticketResponsibleExtraUsers = useMemo(() => {
    const extra = [];
    if (Array.isArray(currentTicket?.responsibleUsers)) {
      extra.push(...currentTicket.responsibleUsers);
    }
    if (currentTicket?.responsibleUser) {
      extra.push(currentTicket.responsibleUser);
    }
    return extra;
  }, [currentTicket?.responsibleUsers, currentTicket?.responsibleUser]);

  // Atualiza o estado interno do ticket sempre que a prop 'ticket' mudar (ex: após re-fetch)
  useEffect(() => {
    setCurrentTicket(ticket);

    // Inicializar lista de responsáveis (suporta responsibleUsers ou responsibleUser único)
    let ids = [];
    if (Array.isArray(ticket?.responsibleUsers) && ticket.responsibleUsers.length > 0) {
      ids = ticket.responsibleUsers.map((u) => normalizeId(u?.id ?? u)).filter(Boolean);
    } else if (ticket?.responsibleUser) {
      const u = ticket.responsibleUser;
      ids = [normalizeId(u?.id ?? u)];
    }
    setSelectedResponsibleUserIds(ids);
    setSelectedPriority(ticket?.priority || '');
    setSelectedAppOrigin(ticket?.appOrigin || '');
    setSelectedCompanyName(ticket?.companyName || '');
    setSelectedDemandType(ticket?.demandType || 'SUPPORT');
    setSelectedDetailedDescription(getTicketDescription(ticket));
  }, [ticket]);

  useEffect(() => {
    setIsEditingDescription(false);
    setAttachmentsTab(ATTACHMENT_TABS[0].id);
    setAttachmentCounts({});
  }, [ticket?.id]);

  const updateAttachmentCount = useCallback((type, count) => {
    setAttachmentCounts((prev) => (prev[type] === count ? prev : { ...prev, [type]: count }));
  }, []);

  useEffect(() => {
    const ticketId = currentTicket?.id;
    if (!ticketId) return undefined;
    let cancelled = false;
    const loaders = ATTACHMENT_TABS.map((tab) => [tab.id, ATTACHMENT_LOADERS[tab.id]]);

    Promise.allSettled(loaders.map(([, load]) => load(ticketId))).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((result, index) => {
        const [type] = loaders[index];
        if (result.status === 'fulfilled') {
          next[type] = Array.isArray(result.value) ? result.value.length : 0;
        } else {
          console.error(`Erro ao contar anexos (${type}) do ticket:`, result.reason);
        }
      });
      setAttachmentCounts((prev) => ({ ...prev, ...next }));
    });

    return () => {
      cancelled = true;
    };
  }, [currentTicket?.id, messagesRefreshKey]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setSaveBarCenter(rect.left + rect.width / 2);
    };
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [currentTicket?.id]);

  if (!currentTicket) {
    return (
      <EmptyState>
        <Inbox size={22} strokeWidth={1.75} aria-hidden />
        Selecione um ticket para ver os detalhes
      </EmptyState>
    );
  }

  // Verifica se o ticket está fechado para desabilitar tudo
  const isTicketClosed = isTerminalStatus(currentTicket.status);
  const ticketProtocol = formatTicketProtocol(currentTicket);
  // IDs atuais de responsáveis no ticket (suporta lista ou único)
  const currentResponsibleUserIds = (() => {
    const fromList = currentTicket?.responsibleUsers;
    if (Array.isArray(fromList) && fromList.length > 0) {
      return fromList.map((u) => normalizeId(u?.id ?? u)).filter(Boolean).sort();
    }
    const single = currentTicket?.responsibleUser;
    if (single != null && single !== '') {
      const id = typeof single === 'object' ? single?.id : single;
      if (id != null && id !== '') return [normalizeId(id)];
    }
    return [];
  })();
  // Botão "Iniciar Atendimento": exibir quando há ao menos um responsável (vindo do ticket ou selecionado na UI)
  const hasResponsibleUsers = currentResponsibleUserIds.length > 0 || (selectedResponsibleUserIds && selectedResponsibleUserIds.length > 0);
  const selectedIdsSorted = [...(selectedResponsibleUserIds || [])].map(normalizeId).filter(Boolean).sort();
  const isResponsibleUsersChanged =
    selectedIdsSorted.length !== currentResponsibleUserIds.length ||
    selectedIdsSorted.some((id, i) => id !== currentResponsibleUserIds[i]);

  // Função para gerar mensagem de alteração
  const generateChangeMessage = (changes) => {
    const userName = currentUser?.name || 'Usuário';
    const changeDescriptions = [];

    if (changes.status) {
      changeDescriptions.push(
        `status para "${getStatusLabel(changes.status) || changes.status}"`
      );
    }

    if (changes.priority) {
      changeDescriptions.push(
        `prioridade para "${getPriorityLabel(changes.priority) || changes.priority}"`
      );
    }

    if (changes.companyName !== undefined) {
      changeDescriptions.push(`empresa para "${changes.companyName || 'Nenhuma'}"`);
    }

    if (changes.demandType) {
      changeDescriptions.push(
        `tipo de demanda para "${getDemandTypeLabel(changes.demandType) || changes.demandType}"`
      );
    }

    if (changes.detailedDescription !== undefined) {
      changeDescriptions.push('a descrição');
    }

    if (changes.resolutionType) {
      changeDescriptions.push(
        `resolução como "${getResolutionTypeLabel(changes.resolutionType) || changes.resolutionType}"`
      );
    }

    if (changes.responsibleUsers && changes.responsibleUsers.length > 0) {
      const names = changes.responsibleUsers
        .map((u) => u?.name || u?.email || 'Usuário')
        .join(', ');
      changeDescriptions.push(`responsáveis para ${names}`);
    } else if (changes.responsibleUser) {
      const responsibleName = changes.responsibleUser.name || changes.responsibleUser.email || 'Usuário';
      changeDescriptions.push(`responsável para "${responsibleName}"`);
    }

    if (changeDescriptions.length === 0) {
      return null;
    }

    const changesText = changeDescriptions.join(', ');
    return `${userName} alterou ${changesText} desse ticket.`;
  };

  // Função para criar mensagem automática
  const createChangeMessage = async (changes) => {
    try {
      const messageContent = generateChangeMessage(changes);
      if (!messageContent) return;

      const finalContent = changes.descriptionChangeMeta
        ? encodeDescriptionChangeMessage(messageContent, {
            oldDescription: changes.descriptionChangeMeta.oldDescription,
            newDescription: changes.descriptionChangeMeta.newDescription,
          })
        : messageContent;

      const messageData = {
        content: finalContent,
        ticketId: currentTicket.id,
        isSupportResponse: true, // Marca como resposta do suporte
        attachments: []
      };

      await messageService.create(messageData);
      console.log('Mensagem de alteração criada com sucesso:', messageContent);

      // Atualizar o refreshKey para recarregar as mensagens
      setMessagesRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Erro ao criar mensagem de alteração:', error);
      // Não vamos mostrar erro ao usuário para não poluir a interface
    }
  };

  const getPriorityClass = (priority) => {
    const priorityMap = {
      'HIGH': 'high',
      'MEDIUM': 'medium',
      'LOW': 'low',
      'CRITICAL': 'critical' // Adicionado
    };
    return priorityMap[priority] || 'medium';
  };

  const isSupportDemandType = (selectedDemandType || currentTicket?.demandType || 'SUPPORT') === 'SUPPORT';

  const requestStatusChange = (newStatus) => {
    // Reopen só envia status; modal de resolução não se aplica a ticket fechado
    if (
      !isTerminalStatus(currentTicket?.status) &&
      requiresResolutionCapture(newStatus)
    ) {
      setPendingResolveStatus(newStatus);
      setShowResolveModal(true);
      return;
    }
    handleStatusChange(newStatus);
  };

  const handleResolveModalCancel = () => {
    if (isStatusChanging) return;
    setShowResolveModal(false);
    setPendingResolveStatus(null);
  };

  const handleResolveModalConfirm = async ({ resolutionType, resolutionNotes }) => {
    if (!pendingResolveStatus) return;
    await handleStatusChange(pendingResolveStatus, { resolutionType, resolutionNotes });
    setShowResolveModal(false);
    setPendingResolveStatus(null);
  };

  // Funções para lidar com as ações dos botões de status
  const handleStatusChange = async (newStatus, resolutionPayload = null) => {
    try {
      setIsStatusChanging(true);
      setChangingStatusTo(newStatus);

      const ticketWasClosed = isTerminalStatus(currentTicket.status);

      if (ticketWasClosed) {
        await reopenTicket(currentTicket.id, newStatus);
      } else {
        const requestPayload = {
          responsibleUsers: Array.isArray(currentTicket.responsibleUsers) ? currentTicket.responsibleUsers : currentTicket.responsibleUser ? [currentTicket.responsibleUser] : [],
          subjectDescription: currentTicket.subjectDescription || currentTicket.subject,
          detailedDescription: selectedDetailedDescription.trim() || getTicketDescription(currentTicket),
          attachments: null,
          userEmail: currentTicket.userEmail,
          appOrigin: selectedAppOrigin || currentTicket.appOrigin,
          companyName: currentTicket.companyName,
          demandType: selectedDemandType || currentTicket.demandType || 'SUPPORT',
          status: newStatus,
          priority: currentTicket.priority,
        };

        if (resolutionPayload) {
          if (resolutionPayload.resolutionType) {
            requestPayload.resolutionType = resolutionPayload.resolutionType;
          }
          if (resolutionPayload.resolutionNotes) {
            requestPayload.resolutionNotes = resolutionPayload.resolutionNotes;
          }
        }

        await updateTicketDetails(currentTicket.id, requestPayload);
      }

      const changeMessagePayload = { status: newStatus };
      if (resolutionPayload?.resolutionType) {
        changeMessagePayload.resolutionType = resolutionPayload.resolutionType;
      }
      await createChangeMessage(changeMessagePayload);

      // Após a alteração de status, re-fetch o ticket completo para obter os novos campos de data/duração
      const updatedTicketData = await getTicketById(currentTicket.id);
      console.log('[SupportTicketDetails] getTicketById após mudança de status:', updatedTicketData);
      setCurrentTicket(updatedTicketData);
      setSelectedDetailedDescription(getTicketDescription(updatedTicketData));
      if (onTicketUpdate) { // Aciona o re-fetch no componente pai também
        await onTicketUpdate(currentTicket.id);
      }
      if (onListUpdate) { // Atualiza a lista principal
        await onListUpdate(true, 1);
      }
    } catch (error) {
      console.error('Erro ao mudar status:', error);
      const apiMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Erro ao mudar status.';
      addToast(apiMessage, 'error');
    } finally {
      setIsStatusChanging(false);
      setChangingStatusTo(null);
    }
  };

  const handlePriorityChange = async (priorityValue) => {
    try {
      const priorityFn = priorityFunctionMap[priorityValue];
      if (priorityFn) {
        await priorityFn(currentTicket.id);

        // Criar mensagem de alteração de prioridade
        await createChangeMessage({ priority: priorityValue });

        const updatedTicketData = await getTicketById(currentTicket.id);
        setCurrentTicket(updatedTicketData);
        setSelectedPriority(updatedTicketData.priority); // Atualiza o select de prioridade com o valor retornado
        if (onTicketUpdate) {
          await onTicketUpdate(currentTicket.id);
        }
        if (onListUpdate) {
          await onListUpdate(true, 1);
        }
      }
    } catch (error) {
      console.error('Erro ao mudar prioridade:', error);
      // Lógica para mostrar erro ao usuário
    }
  };

  const handleResponsibleUserSave = async () => {
    if (!isResponsibleUsersChanged) {
      addToast('Nenhuma mudança nos responsáveis para salvar.', 'info');
      return;
    }
    const selectedUsers = responsibleUserOptions
      .filter((user) => selectedResponsibleUserIds.includes(normalizeId(user.id)))
      .map((user) => ({ id: user.id, name: user.name, email: user.email }))
      .filter(Boolean);
    try {
      await updateResponsibleUsers(currentTicket.id, selectedUsers);
      await createChangeMessage({ responsibleUsers: selectedUsers });

      const updatedTicketData = await getTicketById(currentTicket.id);
      setCurrentTicket(updatedTicketData);
      const nextIds = Array.isArray(updatedTicketData?.responsibleUsers) && updatedTicketData.responsibleUsers.length > 0
        ? updatedTicketData.responsibleUsers.map((u) => normalizeId(u?.id ?? u))
        : updatedTicketData?.responsibleUser
          ? [normalizeId(updatedTicketData.responsibleUser?.id ?? updatedTicketData.responsibleUser)]
          : [];
      setSelectedResponsibleUserIds(nextIds);

      if (onTicketUpdate) await onTicketUpdate(currentTicket.id);
      if (onListUpdate) await onListUpdate(true, 1);
      addToast('Responsáveis atualizados com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao atualizar responsáveis:', error);
      addToast('Falha ao atualizar responsáveis.', 'error');
    }
  };

  const handleDeleteTicket = async () => {
    if (window.confirm('Tem certeza que deseja deletar este ticket? Esta ação não pode ser desfeita.')) {
      try {
        await deleteTicket(currentTicket.id);
        addToast('Ticket deletado com sucesso!', 'success');

        // Limpar seleção e atualizar lista
        if (onResetSelection) {
          onResetSelection();
        }
        if (onListUpdate) {
          await onListUpdate(true, 1);
        }
      } catch (error) {
        console.error('Erro ao deletar ticket:', error);
        addToast('Falha ao deletar ticket.', 'error');
      }
    }
  };


  // Mapeamentos de funções de serviço
  const priorityFunctionMap = {
    // LOW: lowTicket,
    // HIGH: highTicket,
    // CRITICAL: criticalTicket,
    // MEDIUM: mediumTicket
  };

  // Unificar detecção de alteração
  const isPriorityChanged = selectedPriority !== currentTicket.priority;
  const isAppOriginChanged = selectedAppOrigin !== (currentTicket?.appOrigin || '');
  const isCompanyNameChanged = selectedCompanyName !== (currentTicket?.companyName || '');
  const isDemandTypeChanged =
    (selectedDemandType || 'SUPPORT') !== (currentTicket?.demandType || 'SUPPORT');
  const isDescriptionChanged =
    selectedDetailedDescription.trim() !== getTicketDescription(currentTicket).trim();
  const isChanged =
    isResponsibleUsersChanged ||
    isPriorityChanged ||
    isAppOriginChanged ||
    isCompanyNameChanged ||
    isDemandTypeChanged ||
    isDescriptionChanged;

  const getStatusForDemandType = (demandType, definitions = ticketStatuses) => {
    const currentStatus = String(currentTicket?.status || '').toUpperCase();
    const availableDefinitions = Array.isArray(definitions)
      ? definitions
          .filter((definition) => definition?.code && definition.active !== false)
          .map((definition) => ({
            value: String(definition.code).toUpperCase(),
            label: definition.label || definition.code,
            category: definition.category
              ? String(definition.category).toUpperCase()
              : undefined,
            demandTypes: Array.isArray(definition.demandTypes)
              ? definition.demandTypes.map((type) => String(type).toUpperCase())
              : [],
          }))
      : [];
    const compatibleStatuses = availableDefinitions.length > 0
      ? availableDefinitions.filter(
          (status) =>
            status.demandTypes.length === 0 ||
            status.demandTypes.includes(String(demandType).toUpperCase())
        )
      : getStatusFilterOptions(demandType);
    const currentStatusOption = compatibleStatuses.find((option) => option.value === currentStatus);
    if (currentStatusOption) return currentStatusOption.value;

    const currentCategory = getStatusCategory(currentStatus);
    const sameCategoryStatus = compatibleStatuses.find(
      (option) => option.category === currentCategory
    );
    if (sameCategoryStatus) return sameCategoryStatus.value;

    if (availableDefinitions.length > 0) return null;
    return compatibleStatuses[0]?.value || currentStatus;
  };

  // Novo handler centralizado
  const handleSaveChanges = async () => {
    const payloadChanged = {};
    const changes = {};

    if (isResponsibleUsersChanged) {
      const selectedUsers = responsibleUserOptions
        .filter((user) => selectedResponsibleUserIds.includes(normalizeId(user.id)))
        .map((user) => ({ id: user.id, name: user.name, email: user.email }))
        .filter(Boolean);
      payloadChanged.responsibleUsers = selectedUsers;
      changes.responsibleUsers = selectedUsers;
    }

    if (isPriorityChanged) {
      payloadChanged.priority = selectedPriority;
      changes.priority = selectedPriority;
    }

    if (isAppOriginChanged) {
      payloadChanged.appOrigin = selectedAppOrigin;
      changes.appOrigin = selectedAppOrigin;
    }

    if (isCompanyNameChanged) {
      payloadChanged.companyName = selectedCompanyName;
      changes.companyName = selectedCompanyName;
    }

    if (isDemandTypeChanged) {
      payloadChanged.demandType = selectedDemandType;
      changes.demandType = selectedDemandType;
    }

    if (isDescriptionChanged) {
      const newDescription = selectedDetailedDescription.trim();
      payloadChanged.detailedDescription = newDescription;
      changes.detailedDescription = newDescription;
      changes.descriptionChangeMeta = {
        oldDescription: getTicketDescription(currentTicket),
        newDescription,
      };
    }

    if (Object.keys(payloadChanged).length === 0) {
      addToast('Nenhuma alteração para salvar.', 'info');
      return;
    }

    let statusDefinitions = ticketStatuses;
    if (isDemandTypeChanged && (!Array.isArray(statusDefinitions) || statusDefinitions.length === 0)) {
      statusDefinitions = await reloadTicketStatuses?.();
    }

    const nextDemandType =
      payloadChanged.demandType !== undefined
        ? payloadChanged.demandType
        : currentTicket.demandType;
    const nextStatus = getStatusForDemandType(nextDemandType, statusDefinitions);
    if (!nextStatus) {
      addToast('Não há status disponível para o tipo de demanda selecionado.', 'error');
      return;
    }

    const requestPayload = {
      responsibleUsers: payloadChanged.responsibleUsers !== undefined ? payloadChanged.responsibleUsers : (Array.isArray(currentTicket.responsibleUsers) ? currentTicket.responsibleUsers : currentTicket.responsibleUser ? [currentTicket.responsibleUser] : []),
      subjectDescription: currentTicket.subjectDescription || currentTicket.subject,
      detailedDescription:
        payloadChanged.detailedDescription !== undefined
          ? payloadChanged.detailedDescription
          : getTicketDescription(currentTicket),
      attachments: null,
      userEmail: currentTicket.userEmail,
      appOrigin: payloadChanged.appOrigin !== undefined ? payloadChanged.appOrigin : (selectedAppOrigin || currentTicket.appOrigin),
      companyName: payloadChanged.companyName !== undefined ? payloadChanged.companyName : currentTicket.companyName,
      demandType:
        payloadChanged.demandType !== undefined
          ? payloadChanged.demandType
          : selectedDemandType || currentTicket.demandType || 'SUPPORT',
      status: nextStatus,
      priority: payloadChanged.priority !== undefined ? payloadChanged.priority : currentTicket.priority,
    };

    try {
      setIsSaving(true);
      let updatedFromSave;
      const demandTypeOnlyChange = isDemandTypeChanged && Object.keys(payloadChanged).every(
        (field) => field === 'demandType'
      );

      if (demandTypeOnlyChange) {
        const { status: _status, ...demandTypePayload } = requestPayload;
        try {
          updatedFromSave = await updateTicketDetails(currentTicket.id, demandTypePayload);
        } catch (error) {
          const detail = String(
            error?.response?.data?.detail || error?.response?.data?.message || ''
          ).toLowerCase();
          if (!detail.includes('status')) throw error;
          updatedFromSave = await updateTicketDetails(currentTicket.id, requestPayload);
        }
      } else {
        updatedFromSave = await updateTicketDetails(currentTicket.id, requestPayload);
      }

      // Criar mensagem de alteração para múltiplas mudanças
      await createChangeMessage(changes);

      if (onTicketUpdate) await onTicketUpdate(currentTicket.id);
      if (onListUpdate) await onListUpdate(true, 1);

      const updatedTicketData = updatedFromSave?.id
        ? updatedFromSave
        : await getTicketById(currentTicket.id);
      console.log('[SupportTicketDetails] ticket após updateTicketDetails (save):', updatedTicketData);
      setCurrentTicket(updatedTicketData);
      setSelectedPriority(updatedTicketData.priority);
      setSelectedAppOrigin(updatedTicketData.appOrigin || '');
      setSelectedCompanyName(updatedTicketData.companyName || '');
      setSelectedDemandType(updatedTicketData.demandType || 'SUPPORT');
      const nextIds = Array.isArray(updatedTicketData?.responsibleUsers) && updatedTicketData.responsibleUsers.length > 0
        ? updatedTicketData.responsibleUsers.map((u) => normalizeId(u?.id ?? u))
        : updatedTicketData?.responsibleUser
          ? [normalizeId(updatedTicketData.responsibleUser?.id ?? updatedTicketData.responsibleUser)]
          : [];
      setSelectedResponsibleUserIds(nextIds);
      setSelectedDetailedDescription(getTicketDescription(updatedTicketData));
      setIsEditingDescription(false);

      addToast('Alterações salvas com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar alterações do ticket:', e);
      addToast('Erro ao salvar alterações.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setSelectedResponsibleUserIds(currentResponsibleUserIds);
    setSelectedPriority(currentTicket.priority || '');
    setSelectedAppOrigin(currentTicket.appOrigin || '');
    setSelectedCompanyName(currentTicket.companyName || '');
    setSelectedDemandType(currentTicket.demandType || 'SUPPORT');
    setSelectedDetailedDescription(getTicketDescription(currentTicket));
    setIsEditingDescription(false);
  };

  const handleCancelDescriptionEdit = () => {
    setSelectedDetailedDescription(getTicketDescription(currentTicket));
    setIsEditingDescription(false);
  };

  const handleCopyProtocol = async () => {
    if (!ticketProtocol) return;
    try {
      await navigator.clipboard.writeText(ticketProtocol);
      setProtocolCopied(true);
      window.setTimeout(() => setProtocolCopied(false), 1600);
    } catch (error) {
      console.error('Erro ao copiar protocolo:', error);
      addToast('Não foi possível copiar o protocolo.', 'error');
    }
  };

  const changedFieldsCount = [
    isResponsibleUsersChanged,
    isPriorityChanged,
    isAppOriginChanged,
    isCompanyNameChanged,
    isDemandTypeChanged,
    isDescriptionChanged,
  ].filter(Boolean).length;

  const priorityKey = getPriorityClass(currentTicket.priority);
  const statusKey = getStatusClass(currentTicket.status);
  const subject = currentTicket.subjectDescription || currentTicket.subject || 'Sem título';
  const createdAt = currentTicket.creationDate || currentTicket.createdAt;
  const originLabel = currentTicket.appOrigin ? getAppOriginLabel(currentTicket.appOrigin) : '';
  const originColor = getAppOriginColor(currentTicket.appOrigin);
  const selectedResponsibleUsers = responsibleUserOptions.filter((user) =>
    selectedResponsibleUserIds.map(normalizeId).includes(normalizeId(user.id))
  );

  const workflowActions = [];
  if (!isTicketClosed) {
    if (currentCategory === 'INITIAL' && codeInProgress) {
      workflowActions.push({
        key: 'start',
        label: 'Iniciar atendimento',
        icon: Play,
        primary: true,
        status: codeInProgress,
        disabled: !hasResponsibleUsers,
        title: hasResponsibleUsers
          ? undefined
          : 'Atribua um responsável em Propriedades para iniciar o atendimento',
        onClick: () => handleStatusChange(codeInProgress),
      });
    }
    if (currentCategory === 'IN_PROGRESS') {
      if (codePending) {
        workflowActions.push({
          key: 'pending',
          label: 'Marcar pendência',
          icon: CirclePause,
          status: codePending,
          onClick: () => handleStatusChange(codePending),
        });
      }
      if (codeResolved) {
        workflowActions.push({
          key: 'resolve',
          label: 'Finalizar atendimento',
          icon: CircleCheck,
          primary: true,
          status: codeResolved,
          onClick: () => requestStatusChange(codeResolved),
        });
      }
    }
    if (currentCategory === 'PENDING' && codeInProgress) {
      workflowActions.push({
        key: 'resume',
        label: 'Retomar atendimento',
        icon: Play,
        primary: true,
        status: codeInProgress,
        onClick: () => handleStatusChange(codeInProgress),
      });
    }
    if (currentCategory === 'RESOLVED') {
      if (codeInProgress) {
        workflowActions.push({
          key: 'reopen',
          label: 'Reabrir atendimento',
          icon: RotateCcw,
          status: codeInProgress,
          onClick: () => handleStatusChange(codeInProgress),
        });
      }
      if (codeTerminal) {
        workflowActions.push({
          key: 'close',
          label: 'Fechar ticket',
          icon: Lock,
          primary: true,
          status: codeTerminal,
          onClick: () => requestStatusChange(codeTerminal),
        });
      }
    }
  }

  const auditEntries = [
    { key: 'created', label: 'Aberto', date: createdAt },
    { key: 'pendingStart', label: 'Início da pendência', date: currentTicket.inicioPendencia },
    {
      key: 'pendingEnd',
      label: 'Fim da pendência',
      date: currentTicket.fimPendencia,
      detail:
        currentTicket.duracaoPendencia != null
          ? `${formatDuration(currentTicket.duracaoPendencia)} pendente`
          : null,
    },
    { key: 'resolved', label: 'Resolvido', date: currentTicket.dataResolucao },
    {
      key: 'closed',
      label: 'Fechado',
      date: currentTicket.dataFechamento,
      detail:
        currentTicket.duracaoAposResolucao != null
          ? `${formatDuration(currentTicket.duracaoAposResolucao)} após a resolução`
          : null,
    },
  ].filter((entry) => entry.date);

  const renderSelect = ({ id, value, onChange, options, placeholder }) => (
    <SelectWrap>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} strokeWidth={2} aria-hidden />
    </SelectWrap>
  );

  const renderPeople = (people) => (
    <PeopleList>
      {people.map((person) => (
        <PersonRow key={person.id} title={person.email || person.name}>
          <PersonInitial aria-hidden>{(person.name || '?').charAt(0).toUpperCase()}</PersonInitial>
          <span>{person.name}</span>
        </PersonRow>
      ))}
    </PeopleList>
  );

  const handleTabKeyDown = (event, index) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const next = ATTACHMENT_TABS[(index + step + ATTACHMENT_TABS.length) % ATTACHMENT_TABS.length];
    setAttachmentsTab(next.id);
    document.getElementById(`ticket-attachments-tab-${next.id}`)?.focus();
  };

  const attachmentProps = {
    ticketId: currentTicket.id,
    refreshKey: messagesRefreshKey,
    allowUpload: !isTicketClosed,
    compactTitle: true,
  };

  const showSaveBar = isChanged && !isTicketClosed;

  return (
    <Root ref={rootRef}>
      <Header>
        <HeaderMain>
          <ChipsRow>
            {ticketProtocol && (
              <ProtocolChip
                type="button"
                onClick={handleCopyProtocol}
                title="Copiar protocolo"
                aria-label={`Protocolo ${ticketProtocol}. Copiar`}
              >
                #{ticketProtocol}
                {protocolCopied ? (
                  <Check size={12} strokeWidth={2.5} aria-hidden />
                ) : (
                  <Copy size={12} strokeWidth={2} aria-hidden />
                )}
              </ProtocolChip>
            )}
            <StatusPill $status={statusKey}>
              {getStatusLabel(currentTicket.status) || 'Aberto'}
            </StatusPill>
            <PriorityPill $priority={priorityKey}>
              {getPriorityLabel(currentTicket.priority) || 'Média'}
            </PriorityPill>
          </ChipsRow>

          <Title>{subject}</Title>

          <MetaRow>
            {originLabel && (
              <>
                <MetaItem>
                  <OriginDot $color={originColor} aria-hidden />
                  {originLabel}
                </MetaItem>
                <MetaSeparator aria-hidden>·</MetaSeparator>
              </>
            )}
            <MetaItem>{getDemandTypeLabel(currentTicket.demandType) || 'Suporte / Bug'}</MetaItem>
            {currentTicket.userEmail && (
              <>
                <MetaSeparator aria-hidden>·</MetaSeparator>
                <MetaItem>Aberto por {currentTicket.userEmail}</MetaItem>
              </>
            )}
            {createdAt && (
              <>
                <MetaSeparator aria-hidden>·</MetaSeparator>
                <MetaItem as="time" dateTime={createdAt} title={formatDateTime(createdAt)}>
                  {formatRelative(createdAt)}
                </MetaItem>
              </>
            )}
          </MetaRow>
        </HeaderMain>

        <HeaderActions>
          {statusOptions.length > 0 && (
            <SelectWrap $inline>
              <VisuallyHidden as="label" htmlFor="ticket-status-change">
                {isTicketClosed ? 'Reabrir ticket' : 'Alterar status'}
              </VisuallyHidden>
              <select
                id="ticket-status-change"
                value=""
                onChange={(e) => {
                  const next = e.target.value;
                  if (next) requestStatusChange(next);
                  e.target.value = '';
                }}
                disabled={isStatusChanging}
              >
                <option value="">{isTicketClosed ? 'Reabrir como…' : 'Mover para…'}</option>
                {statusOptions
                  .filter((s) => s.value !== currentTicket.status)
                  .map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
              </select>
              <ChevronDown size={14} strokeWidth={2} aria-hidden />
            </SelectWrap>
          )}
          {workflowActions.map(({ key, label, icon: Icon, primary, status, disabled, title, onClick }) => (
            <Button
              key={key}
              type="button"
              $variant={primary ? 'primary' : undefined}
              onClick={onClick}
              disabled={disabled || isStatusChanging}
              title={title}
              aria-busy={changingStatusTo === status || undefined}
            >
              {changingStatusTo === status ? (
                <Spinner size={15} strokeWidth={2} aria-hidden />
              ) : (
                <Icon size={15} strokeWidth={2} aria-hidden />
              )}
              {label}
            </Button>
          ))}
        </HeaderActions>
      </Header>

      {isTicketClosed && (
        <Notice role="status">
          <Lock size={16} strokeWidth={2} aria-hidden />
          <span>
            Este ticket está fechado e os campos estão bloqueados. Para reabrir, use{' '}
            <strong>Reabrir como…</strong> e escolha um status em aberto.
          </span>
          <Button type="button" $variant="danger" $size="sm" onClick={handleDeleteTicket}>
            <Trash2 size={14} strokeWidth={2} aria-hidden />
            Excluir ticket
          </Button>
        </Notice>
      )}

      <Layout>
        <Column>
          <Card aria-labelledby="ticket-description-title">
            <CardHeader>
              <CardTitle id="ticket-description-title">Descrição</CardTitle>
              {!isTicketClosed && !isEditingDescription && (
                <Button
                  type="button"
                  $size="sm"
                  $ghost
                  onClick={() => setIsEditingDescription(true)}
                >
                  <Pencil size={13} strokeWidth={2} aria-hidden />
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardBody>
              {isEditingDescription && !isTicketClosed ? (
                <>
                  <DescriptionTextarea
                    aria-label="Descrição do ticket"
                    value={selectedDetailedDescription}
                    onChange={(e) => setSelectedDetailedDescription(e.target.value)}
                    placeholder="Descreva o problema ou a solicitação em detalhes"
                    autoFocus
                  />
                  <EditFooter>
                    <span>Salve pela barra de alterações na parte inferior.</span>
                    <Button type="button" $size="sm" $ghost onClick={handleCancelDescriptionEdit}>
                      Cancelar edição
                    </Button>
                  </EditFooter>
                </>
              ) : (
                <DescriptionText $empty={!selectedDetailedDescription.trim()}>
                  {selectedDetailedDescription.trim() || 'Nenhuma descrição informada.'}
                </DescriptionText>
              )}
            </CardBody>
          </Card>

          {currentTicket.id && (
            <Card aria-labelledby="ticket-attachments-title">
              <CardHeader>
                <CardTitle id="ticket-attachments-title">Anexos</CardTitle>
              </CardHeader>
              <CardBody>
                <Tabs role="tablist" aria-label="Tipo de anexo">
                  {ATTACHMENT_TABS.map(({ id, label, icon: Icon }, index) => {
                    const count = attachmentCounts[id] || 0;
                    return (
                      <Tab
                        key={id}
                        id={`ticket-attachments-tab-${id}`}
                        type="button"
                        role="tab"
                        aria-selected={attachmentsTab === id}
                        aria-controls="ticket-attachments-panel"
                        aria-label={count > 0 ? `${label}, ${count} ${count === 1 ? 'anexo' : 'anexos'}` : label}
                        tabIndex={attachmentsTab === id ? 0 : -1}
                        onClick={() => setAttachmentsTab(id)}
                        onKeyDown={(event) => handleTabKeyDown(event, index)}
                      >
                        <Icon size={14} strokeWidth={2} aria-hidden />
                        {label}
                        {count > 0 && (
                          <TabBadge key={count} aria-hidden>
                            {count > 99 ? '99+' : count}
                          </TabBadge>
                        )}
                      </Tab>
                    );
                  })}
                </Tabs>
                <AttachmentPanel
                  id="ticket-attachments-panel"
                  role="tabpanel"
                  aria-labelledby={`ticket-attachments-tab-${attachmentsTab}`}
                >
                  {attachmentsTab === 'images' && (
                    <TicketImagesSection
                      {...attachmentProps}
                      title="Imagens"
                      onCountChange={(n) => updateAttachmentCount('images', n)}
                    />
                  )}
                  {attachmentsTab === 'pdf' && (
                    <TicketPdfSection
                      {...attachmentProps}
                      title="Arquivos PDF"
                      onCountChange={(n) => updateAttachmentCount('pdf', n)}
                    />
                  )}
                  {attachmentsTab === 'videos' && (
                    <TicketVideosSection
                      {...attachmentProps}
                      title="Vídeos"
                      onCountChange={(n) => updateAttachmentCount('videos', n)}
                    />
                  )}
                  {attachmentsTab === 'audios' && (
                    <TicketAudiosSection
                      {...attachmentProps}
                      title="Áudios"
                      onCountChange={(n) => updateAttachmentCount('audios', n)}
                    />
                  )}
                </AttachmentPanel>
              </CardBody>
            </Card>
          )}

          {currentTicket.id && (
            <Card id="ticket-messages" aria-labelledby="ticket-messages-title">
              <CardHeader>
                <CardTitle id="ticket-messages-title">Conversa</CardTitle>
              </CardHeader>
              <ConversationBody>
                <TicketMessages
                  ticketId={currentTicket.id}
                  key={currentTicket.id}
                  refreshKey={messagesRefreshKey}
                  onMessageSent={() => setMessagesRefreshKey((k) => k + 1)}
                />
              </ConversationBody>
            </Card>
          )}
        </Column>

        <AsideColumn as="aside" aria-label="Propriedades do ticket">
          <Card aria-labelledby="ticket-properties-title">
            <CardHeader>
              <CardTitle id="ticket-properties-title">Propriedades</CardTitle>
            </CardHeader>
            <CardBody>
              <PropertyList>
                <PropertyRow>
                  <PropertyLabel as="span">Status</PropertyLabel>
                  <ReadValue>
                    <StatusPill $status={statusKey}>
                      {getStatusLabel(currentTicket.status) || 'Aberto'}
                    </StatusPill>
                  </ReadValue>
                </PropertyRow>

                <PropertyRow>
                  <PropertyLabel htmlFor="ticket-priority">
                    Prioridade
                    {isPriorityChanged && <ChangedDot title="Alterado" />}
                  </PropertyLabel>
                  {isTicketClosed ? (
                    <ReadValue>
                      <PriorityPill $priority={priorityKey}>
                        {getPriorityLabel(currentTicket.priority) || 'Média'}
                      </PriorityPill>
                    </ReadValue>
                  ) : (
                    renderSelect({
                      id: 'ticket-priority',
                      value: selectedPriority,
                      onChange: setSelectedPriority,
                      options: PRIORITY_FILTER_OPTIONS,
                    })
                  )}
                </PropertyRow>

                <PropertyRow>
                  <PropertyLabel htmlFor="ticket-demand-type">
                    Tipo
                    {isDemandTypeChanged && <ChangedDot title="Alterado" />}
                  </PropertyLabel>
                  {isTicketClosed ? (
                    <ReadValue>
                      {getDemandTypeLabel(currentTicket.demandType) || 'Suporte / Bug'}
                    </ReadValue>
                  ) : (
                    renderSelect({
                      id: 'ticket-demand-type',
                      value: selectedDemandType,
                      onChange: setSelectedDemandType,
                      options: DEMAND_TYPE_FILTER_OPTIONS,
                    })
                  )}
                </PropertyRow>

                <PropertyRow>
                  <PropertyLabel htmlFor="ticket-app-origin">
                    Aplicação
                    {isAppOriginChanged && <ChangedDot title="Alterado" />}
                  </PropertyLabel>
                  {isTicketClosed ? (
                    <ReadValue $muted={!originLabel}>
                      {originLabel && <OriginDot $color={originColor} aria-hidden />}
                      {originLabel || 'Não informada'}
                    </ReadValue>
                  ) : (
                    renderSelect({
                      id: 'ticket-app-origin',
                      value: selectedAppOrigin,
                      onChange: setSelectedAppOrigin,
                      options: appOriginOptions,
                      placeholder: 'Selecione…',
                    })
                  )}
                </PropertyRow>

                <PropertyRow>
                  <PropertyLabel htmlFor="ticket-company-name">
                    Empresa
                    {isCompanyNameChanged && <ChangedDot title="Alterado" />}
                  </PropertyLabel>
                  {isTicketClosed ? (
                    <ReadValue $muted={!currentTicket.companyName}>
                      {currentTicket.companyName || 'Não informada'}
                    </ReadValue>
                  ) : (
                    <CompanyField>
                      <CompanyNameCombobox
                        id="ticket-company-name"
                        value={selectedCompanyName}
                        onChange={setSelectedCompanyName}
                        placeholder="Digite ou selecione"
                      />
                    </CompanyField>
                  )}
                </PropertyRow>

                <PropertyRow $stacked>
                  <PropertyLabel htmlFor="ticket-details-responsible">
                    Responsáveis
                    {selectedResponsibleUsers.length > 0 && (
                      <PropertyCount>{selectedResponsibleUsers.length}</PropertyCount>
                    )}
                    {isResponsibleUsersChanged && <ChangedDot title="Alterado" />}
                  </PropertyLabel>
                  {isTicketClosed ? (
                    selectedResponsibleUsers.length > 0 ? (
                      renderPeople(selectedResponsibleUsers)
                    ) : (
                      <ReadValue $muted>Ninguém atribuído</ReadValue>
                    )
                  ) : (
                    <ResponsibleField>
                      <ResponsibleMultiSelect
                        users={users}
                        extraUsers={ticketResponsibleExtraUsers}
                        selectedIds={selectedResponsibleUserIds}
                        onSelectedIdsChange={setSelectedResponsibleUserIds}
                        fieldId="ticket-details-responsible"
                        label=""
                        showUserIcon={false}
                        onReloadUsers={reloadUsers}
                        triggerContent={
                          selectedResponsibleUsers.length > 0 ? (
                            renderPeople(selectedResponsibleUsers)
                          ) : (
                            <AssignPlaceholder>
                              <UserPlus size={14} strokeWidth={2} aria-hidden />
                              Atribuir responsável
                            </AssignPlaceholder>
                          )
                        }
                      />
                    </ResponsibleField>
                  )}
                </PropertyRow>
              </PropertyList>
            </CardBody>
          </Card>

          <Card aria-labelledby="ticket-info-title">
            <CardHeader>
              <CardTitle id="ticket-info-title">Detalhes</CardTitle>
            </CardHeader>
            <CardBody>
              <InfoList>
                {ticketProtocol && (
                  <>
                    <dt>Protocolo</dt>
                    <dd>{ticketProtocol}</dd>
                  </>
                )}
                <dt>Solicitante</dt>
                <dd>{currentTicket.userEmail || 'Não informado'}</dd>
                <dt>Aberto em</dt>
                <dd>{formatDateTime(createdAt)}</dd>
              </InfoList>
            </CardBody>
          </Card>

          {(currentTicket.resolutionType || currentTicket.resolutionNotes) && (
            <Card aria-labelledby="ticket-resolution-title">
              <CardHeader>
                <CardTitle id="ticket-resolution-title">Resolução</CardTitle>
              </CardHeader>
              <CardBody>
                {currentTicket.resolutionType && (
                  <InfoList>
                    <dt>Tipo</dt>
                    <dd>{getResolutionTypeLabel(currentTicket.resolutionType)}</dd>
                  </InfoList>
                )}
                {currentTicket.resolutionNotes && <NotesText>{currentTicket.resolutionNotes}</NotesText>}
              </CardBody>
            </Card>
          )}

          {auditEntries.length > 1 && (
            <Card aria-labelledby="ticket-timeline-title">
              <CardHeader>
                <CardTitle id="ticket-timeline-title">Linha do tempo</CardTitle>
              </CardHeader>
              <CardBody>
                <Timeline>
                  {auditEntries.map((entry) => (
                    <TimelineItem key={entry.key}>
                      <strong>{entry.label}</strong>
                      <time dateTime={entry.date}>{formatDateTime(entry.date)}</time>
                      {entry.detail && <span>{entry.detail}</span>}
                    </TimelineItem>
                  ))}
                </Timeline>
              </CardBody>
            </Card>
          )}
        </AsideColumn>
      </Layout>

      {showSaveBar && (
        <SaveBar role="status" style={saveBarCenter != null ? { left: saveBarCenter } : undefined}>
          <SaveBarText>
            {changedFieldsCount === 1
              ? '1 alteração não salva'
              : `${changedFieldsCount} alterações não salvas`}
          </SaveBarText>
          <SaveBarButton type="button" onClick={handleDiscardChanges} disabled={isSaving}>
            Descartar
          </SaveBarButton>
          <SaveBarButton type="button" $primary onClick={handleSaveChanges} disabled={isSaving}>
            {isSaving ? (
              <Spinner size={14} strokeWidth={2} aria-hidden />
            ) : (
              <Check size={14} strokeWidth={2.5} aria-hidden />
            )}
            {isSaving ? 'Salvando…' : 'Salvar alterações'}
          </SaveBarButton>
        </SaveBar>
      )}

      <ResolveTicketModal
        open={showResolveModal}
        requireResolutionType={isSupportDemandType}
        submitting={isStatusChanging}
        targetStatusLabel={getStatusLabel(pendingResolveStatus) || ''}
        onCancel={handleResolveModalCancel}
        onConfirm={handleResolveModalConfirm}
      />
    </Root>
  );
};

SupportTicketDetails.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.string,
    protocolo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    protocol: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    protocolNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subjectDescription: PropTypes.string,
    subject: PropTypes.string,
    detailedDescription: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.string,
    creationDate: PropTypes.string,
    createdAt: PropTypes.string,
    appOrigin: PropTypes.string,
    userEmail: PropTypes.string,
    responsibleUser: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string
    }),
    // Adicionar PropTypes para os novos campos de auditoria
    inicioPendencia: PropTypes.string,
    fimPendencia: PropTypes.string,
    duracaoPendencia: PropTypes.number, // Ou string, dependendo de como você armazenou
    dataResolucao: PropTypes.string,
    dataFechamento: PropTypes.string,
    duracaoAposResolucao: PropTypes.number,
    resolutionType: PropTypes.string,
    resolutionNotes: PropTypes.string,
  }),
  onTicketUpdate: PropTypes.func,
  onListUpdate: PropTypes.func,
  onResetSelection: PropTypes.func,
  users: PropTypes.array
};

export default SupportTicketDetails;